import React, { useState, useRef } from 'react';
import { Form, Field } from 'react-final-form';
import arrayMutators from 'final-form-arrays';
import { FieldArray } from 'react-final-form-arrays';
import { Link } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
import Button, {
  buttonTypes,
  buttonSizes,
  buttonFunctions,
  buttonTextSizes,
} from '../../shared/Button';
import InlineHelpText from '../../shared/InlineHelpText';

import {
  searchText,
  submitText,
  cancelText,
  titleText,
} from '../../shared/SharedInternationalizations';
import HelpIcon from '../../shared/HelpIcon';
import Table from '../../shared/table/Table';
import { selectorTypes } from '../../shared/table/TableRow';
import SvgXIcon from '../../svg/XIcon';
import SvgUpArrowIcon from '../../svg/UpArrowIcon';
import SvgDownArrowIcon from '../../svg/DownArrowIcon';
import sort from '../../../utilities/sort';

function CategoryDisplayForm(props) {
  const {
    user,
    onSubmit,
    categories,
    categoriesArr = [],
    categoryDisplay,
    previous,
  } = props;

  const [
    showSelectedCategoriesHelpText,
    setShowSelectedCategoriesHelpText,
  ] = useState(false);
  function toggleSelectedCategoriesHelpText(value) {
    setShowSelectedCategoriesHelpText(value);
  }
  const selectedCategoriesHelpTextRef = useRef();
  const selectedCategoriesHelpText = (
    <FormattedMessage
      id="categoryDisplay.selectedCategoriesHelpText"
      defaultMessage="These are categories that are selected"
    />
  );
  const selectedCategoriesText = (
    <FormattedMessage
      id="categoryDisplay.selectedCategoriesText"
      defaultMessage="Selected Categories"
    />
  );
  const indexText = (
    <FormattedMessage
      id="categoryDisplay.indexText"
      defaultMessage="Display Order"
    />
  );

  const initialCategories = Object.values(categoryDisplay).filter(
    (cat) => cat.username === user.username
  );

  const sortedSelectedCategories = sort(
    initialCategories,
    'category_order',
    'az'
  );

  const selectedCategories = sortedSelectedCategories.map(
    (c) => c.alarm_device_type
  );

  const [categoriesFilter, setCategoriesFilter] = useState('');

  /**
   * Function to set the search value for active filtering for users
   * @param {object} event - The event from typing in the value to the search bar
   * @fires setUsersFilter - Updates local state with the value typed into the search bar
   */
  const categoriesSearchFilter = (event) => {
    const value = event.target.value;
    setCategoriesFilter(value);
  };

  /**
   * Function to generate the users table
   * @returns {object} - The table of users
   */
  const categoriesTable = (values) => {
    const initialFilterList = categoriesArr.filter((d) => {
      if (values.categories.includes(d.alarm_device_type)) {
        return false;
      } else {
        return true;
      }
    });

    const categoriesArray = initialFilterList.map((d) => {
      return {
        selector: {
          sortBy: d.alarm_device_type,
          display: d.alarm_device_type,
        },

        title: {
          sortBy: d.description.toUpperCase(),
          display: d.description,
        },
      };
    });

    let searchedCategories = [];

    // Take the array and filter based on the text entered in the search bar.
    if (categoriesFilter !== '') {
      searchedCategories = categoriesArray.filter((item) => {
        let filterResult = false;
        // Make the device name uppercase for case insensitivity
        const title = item.title.display.toUpperCase();

        // filter based off the includes() value with the entered text (also switched to uppercase) as the filter parameter
        if (title.includes(categoriesFilter.toUpperCase())) {
          filterResult = true;
        }
        return filterResult;
      });
    } else {
      searchedCategories = categoriesArray;
    }

    const tableHeadings = [
      {
        isSortable: false,
        display: '',
      },

      {
        isSortable: true,
        sortBy: 'title',
        display: titleText,
      },
    ];
    // generates the actual table
    const table = (
      <Table
        hasTableHead={true}
        hasSelector={true}
        tableHeadings={tableHeadings}
        selectorName="categories"
        selectorParamValue="categories"
        sortParam="title"
        tableData={searchedCategories}
        selectorType={selectorTypes.checkbox}
        tableStyles="table__constrained table-layout__1-column--selector"
        data-test="categories-table"
      />
    );

    return table;
  };

  /**
   * Function to generate the parent unit table
   * @returns {object} - The table of parent units
   */
  const selectedCategoriesTable = (values, selectedCategories) => {
    let table;
    let categoriesRows;

    if (selectedCategories.length > 0) {
      if (selectedCategories) {
        categoriesRows = selectedCategories.map((d, index) => {
          const buttons = (
            <div className="flex-row align-center">
              <label>
                <Field className="visually-hidden" name={d} component="input" />
                <SvgXIcon onClick={() => selectedCategories.remove(index)} />
              </label>
              <div className="flex-spread width-100">
                {index + 1 < selectedCategories.length ? (
                  <Button
                    buttonType={buttonTypes.danger}
                    onClick={() => selectedCategories.swap(index, index + 1)}
                    buttonSize={buttonSizes.extraSmall}
                    text={<SvgDownArrowIcon />}
                  />
                ) : (
                  <div />
                )}
                {index > 0 ? (
                  <Button
                    buttonType={buttonTypes.success}
                    onClick={() => selectedCategories.swap(index, index - 1)}
                    buttonSize={buttonSizes.extraSmall}
                    text={<SvgUpArrowIcon />}
                  />
                ) : (
                  <div />
                )}
              </div>
            </div>
          );

          return {
            selector: {
              sortBy: '',
              display: buttons,
            },

            title: {
              sortBy: d.toUpperCase(),
              display: categories[values.categories[index]].description,
            },
            id: {
              sortBy: index,
              display: index + 1,
            },
          };
        });
      }

      const tableHeadings = [
        {
          isSortable: false,
          display: '',
        },

        {
          isSortable: false,
          sortBy: 'title',
          display: titleText,
        },
        {
          isSortable: false,
          sortBy: 'id',
          display: indexText,
        },
      ];
      // generates the actual table
      table = (
        <Table
          hasTableHead={true}
          hasSelector={false}
          tableHeadings={tableHeadings}
          sortParam="id"
          tableData={categoriesRows}
          tableStyles="table__constrained table-layout__2-column--selector"
          data-test="selected-categories-table"
        />
      );

      return table;
    } else {
      return '';
    }
  };

  return (
    <div className="content-display__container">
      <Form
        data-test="categories-form"
        onSubmit={onSubmit}
        mutators={{
          ...arrayMutators,
        }}
        keepDirtyOnReinitialize={true}
        initialValues={{
          categories: selectedCategories || [],
          initialCategories: selectedCategories || [],
        }}
        render={({
          handleSubmit,
          pristine,
          invalid,
          reset,
          submitting,
          values,
        }) => (
          <form
            onSubmit={handleSubmit}
            className="associate-users-units__form-container">
            <Field
              className="visually-hidden"
              name="associateId"
              component="input"
              type="text"
            />
            <Field
              className="visually-hidden"
              name="initialUsers"
              component="input"
              type="select"
            />
            <div className="form__section-container form__section-container--grid">
              <div className="form__item-container">
                <div className="align-center">
                  <label
                    className="text-medium text-bold form-item__field-message--required"
                    htmlFor="usersId">
                    {selectedCategoriesText}
                  </label>
                  <HelpIcon
                    onClick={() =>
                      selectedCategoriesHelpTextRef.current.toggleHelpText()
                    }
                    toggled={showSelectedCategoriesHelpText}
                    styles="margin-left-5"
                  />
                </div>
              </div>
            </div>
            <div className="width-100 margin-bottom-10">
              <InlineHelpText
                ref={selectedCategoriesHelpTextRef}
                toggleFunction={toggleSelectedCategoriesHelpText}
                message={selectedCategoriesHelpText}
              />
            </div>
            <div className="form__section-container form__section-container--grid margin-bottom-10">
              <FieldArray name="categories">
                {({ fields }) => selectedCategoriesTable(values, fields)}
              </FieldArray>
              <div className="table__header padding-10">
                <label className="text-medium text-bold" htmlFor="usersFilter">
                  {searchText}:
                </label>

                <input
                  type="text"
                  id="categoriesFilter"
                  name="categoriesFilter"
                  onChange={categoriesSearchFilter}
                  className="message-table__filter-search-box margin-left-10"
                  value={categoriesFilter}
                />
              </div>

              {categoriesTable(values)}
            </div>
            <div className="form__section-container form__section-container--spread associate-users-units__form-categories-container">
              <div className="form__items-group-container" />
              <div className="form__items-group-container">
                <Link
                  className="link link-text"
                  to={previous}
                  data-test="associate-devices-form-cancel-link">
                  {cancelText}
                </Link>
                <Button
                  buttonFunction={buttonFunctions.submit}
                  text={submitText}
                  buttonType={buttonTypes.success}
                  buttonSize={buttonSizes.small}
                  buttonTextSize={buttonTextSizes.mediumSmall}
                  additionalStyles="no-margin margin-left-15"
                  disabled={pristine || invalid}
                />
              </div>
            </div>
          </form>
        )}
      />
    </div>
  );
}

export default CategoryDisplayForm;
